import { Box } from "@mui/material";
import { useEffect, useMemo } from "react";

import { useScopeOfWork } from "../../context/ScopeOfWorkContext";
import {
  ScopeOfWorkStep,
} from "../../context/scope-of-work.types";
import { SCOPE_OF_WORK_CONFIG } from "../../config/scope-of-work.config";

import { Categories } from "../categories/Categories";
import { TypeOfPlaces } from "../type-of-places/TypeOfPlaces";
import { TypeOfService } from "../type-of-service/TypeOfService";
import { Areas } from "../areas/Areas";
import { Objects } from "../objects/Objects";
import { SpecialRequests } from "../special-request/SpecialRequest";
import { ScopeOfWorkSummary } from "../summary/ScopeOfWorkSummary";
import { VerticalStepper } from "../VerticalStepper";
import { StepTitle } from "../StepTitle";
import { useQuery } from "@tanstack/react-query";
import { fetchSpecialRequests } from "../special-request/actions/fetchSpecialRequests.actions";

type Props = {
  lockCategory?: boolean;
  showSummary?: boolean;
  job?: any;
  jobAlias?: string;
  setJobAlias?: (val: string) => void;
  jobDescription?: string;
  setJobDescription?: (val: string) => void;
};

export const ScopeOfWorkOrchestrator = ({
  lockCategory = false,
  showSummary = false,
  job,
  jobAlias,
  setJobAlias,
  jobDescription,
  setJobDescription,
}: Props) => {
  const { state, dispatch } = useScopeOfWork();

const {
  step,
  category,
  places,
  service,
} = state;

const categoryParam =
  category?.uid ?? category?.name ?? "";

const specialRequestsQuery = useQuery({
  queryKey: [
    "scope-of-work-special-requests-count",
    categoryParam,
  ],
  queryFn: () =>
    fetchSpecialRequests(
      1,
      1,
      categoryParam
        ? { category: categoryParam }
        : undefined,
    ),
  enabled: Boolean(categoryParam),
  staleTime: 1000 * 60 * 5,
  retry: 1,
});

const specialRequestsTotal =
  specialRequestsQuery.data?.totalItems;

const shouldShowSpecialRequests =
  !categoryParam ||
  specialRequestsTotal === undefined ||
  specialRequestsTotal > 0;

const stepsConfig = useMemo(() => {
  return SCOPE_OF_WORK_CONFIG.filter((configStep) => {
    if (
      configStep.id === ScopeOfWorkStep.SpecialRequests &&
      !shouldShowSpecialRequests
    ) {
      return false;
    }

    if (
      configStep.id === ScopeOfWorkStep.Summary &&
      !showSummary
    ) {
      return false;
    }

    return true;
  });
}, [shouldShowSpecialRequests, showSummary]);

  useEffect(() => {
    const currentStepExists = stepsConfig.some(
      (configStep) => configStep.id === step,
    );

    if (currentStepExists || stepsConfig.length === 0) {
      return;
    }

    const fallbackStep =
      stepsConfig[stepsConfig.length - 1]?.id;

    if (fallbackStep) {
      dispatch({
        type: "SET_STEP",
        payload: fallbackStep,
      });
    }
  }, [dispatch, step, stepsConfig]);

  const handleStepClick = (stepId: ScopeOfWorkStep) => {
    dispatch({
      type: "SET_STEP",
      payload: stepId,
    });
  };

  const stepComponents: Record<
    ScopeOfWorkStep,
    React.ReactNode
  > = {
    [ScopeOfWorkStep.Categories]: (
      <Categories
        selected={category}
        lockCategory={lockCategory}
      />
    ),

    [ScopeOfWorkStep.TypeOfPlaces]: (
      <TypeOfPlaces
        selected={places}
        categoryType={category?.name}
      />
    ),

    [ScopeOfWorkStep.TypeOfService]: (
      <TypeOfService
        selected={service}
        categoryType={category?.name}
      />
    ),

    [ScopeOfWorkStep.Areas]: <Areas />,

    [ScopeOfWorkStep.Objects]: <Objects />,

    [ScopeOfWorkStep.SpecialRequests]: (
      <SpecialRequests />
    ),

    [ScopeOfWorkStep.Summary]: (
      <ScopeOfWorkSummary
        job={job}
        jobAlias={jobAlias}
        setJobAlias={setJobAlias}
        jobDescription={jobDescription}
        setJobDescription={setJobDescription}
      />
    ),
  };

  const currentStepConfig = stepsConfig.find(
    (configStep) => configStep.id === step,
  );

  return (
    <Box
      sx={{
        display: "flex",
        width: "100%",
        height: "100%",
        mt: 3,
      }}
    >
      <Box
        sx={{
          width: "20%",
          background: "var(--fx-surface-base, #FFFFFF)",
          zIndex: 10,
          mt: 4,
        }}
      >
        <VerticalStepper
          steps={stepsConfig}
          currentStep={step}
          onStepClick={handleStepClick}
        />
      </Box>

      <Box
        sx={{
          width: "100%",
          overflowY: "auto",
          mt: 4,
        }}
      >
        <StepTitle
          title={currentStepConfig?.label ?? ""}
          subtitle={currentStepConfig?.description ?? ""}
        />

        <Box sx={{ px: 4, pb: 4 }}>
          {stepComponents[step] ?? null}
        </Box>
      </Box>
    </Box>
  );
};